import {
  BiVisualizationBody,
  kBiEmptyResultText,
} from '@euler/app/components/message/widgets/bi-visualization/BiVisualizationBody';
import { CopyTableButton } from '@euler/app/components/message/widgets/bi-visualization/CopyTableButton';
import { BiViewMode } from '@euler/app/components/message/widgets/bi-visualization/functions/biViewMode';
import { biTableOfViz } from '@euler/app/components/message/widgets/bi-visualization/functions/biVizTable';
import { Typography } from '@euler/app/components/typography';
import {
  CardLoading,
  CardNotice,
} from '@euler/app/flows/ai-suite/components/ChatCard';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { BiCardQuery, isBiCardQueryLimited } from '@euler/model/bi/blend';
import { BiPalette, BiVizSpec } from '@euler/model/bi/viz';
import { Inbox, Lock } from 'lucide-react-native';
import { ReactNode, memo, useMemo } from 'react';
import { ScrollView, View } from 'react-native';
import { BiRetry } from '../../components/ui/BiRetry';
import { BiCardKind } from '../../functions/biCardKind';
import { BiCardDataView } from '../functions/useBiCardData';

/** What a card shows under its title, by the state of its data. */
export const CardBody = memo(
  ({
    state,
    spec,
    viz,
    kind,
    compact,
    view = BiViewMode.Chart,
    palette,
    chartHeight,
    scroll = true,
    onRetry,
  }: {
    state: BiCardDataView;
    /** The card's query; a sample without one shows the cut as it is. */
    spec?: BiCardQuery;
    viz: BiVizSpec;
    kind: BiCardKind;
    compact?: boolean;
    /** A chart card toggled to its result table scrolls like a table. */
    view?: BiViewMode;
    palette?: BiPalette;
    /** The plot's height, e.g. taller on the card page. */
    chartHeight?: number;
    /**
     * Inside a fixed-height card a table scrolls and a chart fits the room
     * left under its title; a page scrolls itself.
     */
    scroll?: boolean;
    onRetry: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const ready = state.kind === 'ready' ? state : null;
    // A table scrolling inside a fixed-height card floats its 复制数据 over
    // the corner, in reach without scrolling to the last row.
    const scrollsTable =
      scroll && (kind === BiCardKind.Table || view === BiViewMode.Table);
    const floatingTable = useMemo(
      () =>
        scrollsTable && ready?.result.current.rows.length
          ? biTableOfViz(viz, ready.result.current)
          : null,
      [scrollsTable, ready, viz],
    );
    // A state with no data sits in the middle of the room under the title.
    const centered = (children: ReactNode) => (
      <View
        style={{
          flex: scroll ? 1 : undefined,
          minHeight: scroll ? undefined : rpx(120),
          alignItems: 'center',
          justifyContent: 'center',
          gap: rpx(8),
          paddingHorizontal: rpx(8),
        }}
      >
        {children}
      </View>
    );
    switch (state.kind) {
      case 'loading':
        return centered(<CardLoading label="正在读取数据…" />);
      case 'failed':
        return centered(
          <>
            <CardNotice tone="danger" text={state.message} center />
            <BiRetry label="重新加载" onRetry={onRetry} />
          </>,
        );
      case 'restricted':
        return centered(
          <View
            style={{ flexDirection: 'row', alignItems: 'center', gap: rpx(6) }}
          >
            <Lock size={13} color={aiColors.mutedForeground} />
            <Typography.Text
              size={rpx(12)}
              color={aiColors.mutedForeground}
              style={{ flexShrink: 1 }}
            >
              你的角色不能查看这张图表的数据
            </Typography.Text>
          </View>,
        );
      case 'ready': {
        if (state.result.current.rows.length === 0)
          return centered(
            <>
              <Inbox
                size={rpx(28)}
                strokeWidth={1.5}
                color={aiColors.mutedForeground}
              />
              <CardNotice text={kBiEmptyResultText} center />
            </>,
          );
        const body = (
          <>
            <BiVisualizationBody
              viz={viz}
              result={state.result}
              compact={compact}
              view={view}
              palette={palette}
              chartHeight={chartHeight}
              fill={scroll && chartHeight === undefined}
              inlineCopy={!floatingTable}
            />
            {state.result.current.truncated &&
            !(spec && isBiCardQueryLimited(spec)) ? (
              <CardNotice text="符合条件的数据超过上限，这里只包含部分结果。" />
            ) : null}
          </>
        );
        if (scrollsTable)
          return (
            <View style={{ flex: 1 }}>
              <ScrollView
                style={{ flex: 1 }}
                contentContainerStyle={{
                  gap: rpx(8),
                  // The last row scrolls clear of the floating button.
                  paddingBottom: floatingTable ? rpx(36) : 0,
                }}
              >
                {body}
              </ScrollView>
              {floatingTable ? (
                <View
                  style={{
                    position: 'absolute',
                    right: rpx(12),
                    bottom: rpx(4),
                  }}
                >
                  <CopyTableButton table={floatingTable} floating />
                </View>
              ) : null}
            </View>
          );
        return (
          <View
            style={{
              gap: rpx(8),
              flex: scroll ? 1 : undefined,
              // A body shorter than the card sits mid-card, not under the
              // title; a chart that fills the card is unaffected.
              justifyContent: 'center',
            }}
          >
            {body}
          </View>
        );
      }
    }
  },
);
